import { numericFilterPrecision } from "./facets.js";
import { selectedUnits } from "./units-conversion.js";

// Resolve a precision config (a number, or a { imperial, metric } map keyed by
// the currently selected unit system) to a non-negative integer, or null when
// the config is absent/invalid so callers can fall through to the next source.
const precisionFromConfig = (precisionConfig) => {
  if (Number.isFinite(precisionConfig)) {
    return Math.max(0, Math.round(precisionConfig));
  }

  if (precisionConfig && typeof precisionConfig === "object") {
    const precision = precisionConfig[selectedUnits()];

    if (Number.isFinite(precision)) {
      return Math.max(0, Math.round(precision));
    }
  }

  return null;
};

// Column display precision. Precedence: category assignment entry -> global
// facet -> default numericFilterPrecision ({ imperial: 3, metric: 1 }).
export const resolveColumnPrecision = (facet, assignment) =>
  precisionFromConfig(assignment?.precision) ??
  precisionFromConfig(facet?.precision) ??
  precisionFromConfig(numericFilterPrecision);

// Skip flag. Precedence: category assignment entry -> global facet -> false.
// A category `false` explicitly overrides a global `true` (nullish coalescing).
const resolveSkipNumericFormatting = (facet, assignment) =>
  assignment?.skipNumericFormatting ?? facet?.skipNumericFormatting ?? false;

// Strict: the entire trimmed value must be a plain number (optional sign, one
// optional decimal point). Anything containing letters, units, fractions,
// ranges, comparison operators, or thousands separators is returned unchanged.
const STRICT_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)$/;

const formatNumericValue = (raw, precision) => {
  if (raw == null) {
    return raw;
  }

  const value = String(raw).trim();

  if (!STRICT_NUMBER.test(value)) {
    return raw;
  }

  return Number(value).toFixed(precision);
};

// Attach a `formatValue` formatter to a column (in place) when numeric
// formatting applies. When the facet/assignment opts out we leave `formatValue`
// unset; renderers already fall back to the raw value, so no identity formatter
// is needed.
export const attachColumnFormatValue = (column, facet, assignment) => {
  if (resolveSkipNumericFormatting(facet, assignment)) {
    return;
  }

  const precision = resolveColumnPrecision(facet, assignment);
  column.formatValue = (value) => formatNumericValue(value, precision);
};
